import { useEffect, useState } from 'react'
import { Loader2, LoaderCircle, Check, Trash2, Wand2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { AIIcon } from '@/components/icons/AIIcon'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertTitle } from '@/components/ui/alert'
import { HoverableMediaPreview } from '@/components/ui/hoverable-media-preview'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import PromptHelper from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/PromptHelper'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import GenerationProgress from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/GenerationProgress'
import { useVideoGeneration } from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/useVideoGeneration'
import RefineMediaDialog from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/RefineMediaDialog'
import { useLazyQuery, useMutation } from '@apollo/client'
import { MIRROR_AS_AD_CREATIVE_ASSET } from '@/graphql/ads/generated-image-queries'
import { GET_AD_ASSETS, DELETE_AD_ASSET } from '@/graphql/ads/ad-assets-queries'
import { showNotification } from '@/utils/notifications'

const FORMAT_LABELS = { square: 'Square (1:1)', vertical: 'Vertical (9:16)', horizontal: 'Horizontal (16:9)' }
const PREVIEW_HEIGHT = 160
const PREVIEW_WIDTHS = { square: 160, vertical: 90, horizontal: 284 }
// A 9:16 preview is tall, so it is enlarged less
const ENLARGED_SIZES = { vertical: 'small' }
const ASPECT_RATIOS = { square: 1, vertical: 9 / 16, horizontal: 16 / 9 }
const DRAFT_ASSET_STEPS = ['setVideoThumbnail', 'videoTrim', 'setMediaCrop']

const VideoItem = ({ video, isSelected, onSelect, onDelete, onRefine }) => {
    const isProcessing = video.status === 'processing'
    const isCompleted = video.status === 'completed'
    const previewSize = { width: PREVIEW_WIDTHS[video.format] ?? PREVIEW_WIDTHS.horizontal, height: PREVIEW_HEIGHT }

    return (
        <div className="tw:flex tw:flex-col tw:gap-2 tw:relative" style={{ maxWidth: previewSize.width }}>
            {isProcessing && (
                <div
                    className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:bg-gray-600 tw:rounded tw:p-2 tw:gap-2"
                    style={previewSize}
                >
                    <LoaderCircle size={24} className="tw:animate-spin tw:text-blue-400" />
                    <span className="tw:text-xs tw:text-gray-300">Generating video...</span>
                    <span className="tw:text-xs tw:text-gray-400">This may take 1-3 minutes</span>
                </div>
            )}

            {isCompleted && (
                <div
                    className={`tw:relative tw:cursor-pointer tw:rounded tw:overflow-hidden tw:border-2 ${isSelected ? 'tw:border-blue-400' : 'tw:border-transparent'}`}
                    onClick={() => onSelect(video.id)}
                >
                    <HoverableMediaPreview
                        src={import.meta.env.VITE_S3_BUCKET_URL + video.url}
                        mediaType="video"
                        tooltipSize={ENLARGED_SIZES[video.format] ?? 'medium'}
                        aspectRatio={ASPECT_RATIOS[video.format] ?? ASPECT_RATIOS.horizontal}
                        expandedClassName="tw:rounded-xs"
                    >
                        <video
                            src={import.meta.env.VITE_S3_BUCKET_URL + video.url}
                            className="tw:object-cover tw:block"
                            style={previewSize}
                            muted
                            loop
                            playsInline
                            onMouseEnter={(e) => e.target.play()}
                            onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0 }}
                        />
                    </HoverableMediaPreview>
                    {isSelected && (
                        <div className="tw:absolute tw:top-1 tw:left-1 tw:bg-blue-400 tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center">
                            <Check size={14} />
                        </div>
                    )}
                    <button
                        type="button"
                        aria-label="Refine video"
                        onClick={(e) => { e.stopPropagation(); onRefine(video) }}
                        className="tw:absolute tw:top-1 tw:right-8 tw:bg-gray-300/60 tw:hover:bg-gray-300 tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center"
                    >
                        <Wand2 size={14} />
                    </button>
                    <button
                        aria-label="Delete video"
                        onClick={(e) => { e.stopPropagation(); onDelete([video.id]) }}
                        className="tw:absolute tw:top-1 tw:right-1 tw:bg-gray-300/60 tw:hover:bg-gray-300 tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            )}

            <div className="tw:text-sm tw:text-gray-300 tw:truncate">{FORMAT_LABELS[video.format] || video.format}</div>
        </div>
    )
}

const AiVideoGeneration = () => {
    const {
        goToStep,
        registerCustomNavigation,
        clearCustomNavigation,
        markStepAs,
        setStepContentData,
        getStepContentData,
        removeStepContentData,
        dataForStepper,
        campaignId,
        id: adCreativeId,
        editCreativeId,
    } = useWizard()
    const [fetchDraftAssets] = useLazyQuery(GET_AD_ASSETS, { fetchPolicy: 'no-cache' })
    const [deleteAdAsset] = useMutation(DELETE_AD_ASSET)

    // A video picked for this draft was copied into it as an ad asset; deleting the video must remove that copy too
    const detachDeletedVideosFromDraft = async (videoIds) => {
        if (editCreativeId || !adCreativeId) return

        try {
            const { data } = await fetchDraftAssets({ variables: { campaignId, adId: adCreativeId } })
            const copies = (data?.AdAsset?.adAssets || []).filter(
                (asset) => asset.source === 'ai' && videoIds.includes(asset.source_id),
            )
            if (copies.length === 0) return

            const copyIds = copies.map((asset) => asset.id)
            await Promise.all(copyIds.map((id) => deleteAdAsset({ variables: { id } })))
            DRAFT_ASSET_STEPS.forEach((step) => {
                if (copyIds.includes(getStepContentData(step)?.assetId)) removeStepContentData(step, 'assetId')
            })
        } catch {
            showNotification('The video was deleted but could not be removed from this ad.', 'error', 5000, null, null, false, true)
        }
    }

    const {
        selectedFormats,
        prompt,
        isGenerating,
        generatedVideos,
        selectedVideos,
        showDeleteConfirmationModal,
        loading,
        generationProgress,
        setPrompt,
        handleFormatSelection,
        handleGenerate,
        handleStopGenerate,
        handleDelete,
        handleConfirmDelete,
        handleSelect,
        setShowDeleteConfirmationModal,
        replaceAsset,
        trackGenerating,
    } = useVideoGeneration({ onVideosDeleted: detachDeletedVideosFromDraft })
    const [isMirroring, setIsMirroring] = useState(false)
    const [refining, setRefining] = useState(null)

    const [mirrorMedia] = useMutation(MIRROR_AS_AD_CREATIVE_ASSET, {
        onCompleted: (data) => {
            setIsMirroring(false)
            const newAssetId = data.mirrorAsAdCreativeAsset[0].id
            setStepContentData({ assetId: newAssetId }, 'videoTrim')
            markStepAs(1)
            goToStep('videoTrim')
        },
        onError: (error) => {
            setIsMirroring(false)
            const message = error.graphQLErrors?.[0]?.message || error.message || 'Failed to process video.'
            showNotification(message, 'error', 5000, null, null, false, true)
        },
    })

    const handleNext = async () => {
        if (selectedVideos.length === 0) {
            showNotification('Please select a video to continue', 'error', 3000, null, null, false, true)
            return
        }
        setIsMirroring(true)
        const replaceExisting = !!editCreativeId
        const replaceFormat = getStepContentData('uploadAssets')?.replaceFormat
        if (replaceFormat) removeStepContentData('uploadAssets', 'replaceFormat')

        await mirrorMedia({
            variables: {
                ids: selectedVideos,
                campaignId,
                adId: adCreativeId,
                replaceExisting,
                allowMultiple: false,
                formats: replaceFormat ? [replaceFormat] : [],
                source: 'aiVideo',
            },
        })
    }

    const Footer = () => (
        <div className="tw:w-full tw:flex tw:justify-end tw:gap-4">
            <Button variant="secondary" className="tw:w-[156px] tw:!text-sm" onClick={() => { markStepAs(0); goToStep('uploadAssets') }}>
                Back
            </Button>
            <Button onClick={handleNext} className="tw:w-[156px] tw:!text-sm" disabled={isMirroring}>
                {isMirroring ? <LoaderCircle size={16} className="tw:animate-spin" /> : 'Next'}
            </Button>
        </div>
    )

    useEffect(() => markStepAs(0), [])

    useEffect(() => {
        registerCustomNavigation(<Footer />)
        return () => clearCustomNavigation()
    }, [selectedVideos, isMirroring])

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="aiTextToVideo" />
            <div className="flex-grow-1 tw:px-6 tw:py-4 overflow-auto">
                <span className="tw:text-base tw:font-semibold">Generate Video from Text Prompt</span>

                <div className="tw:mt-4">
                    <span className="tw:text-sm tw:font-semibold">Aspect Ratio</span>
                    <div className="tw:flex tw:space-x-8 tw:mt-4">
                        {Object.entries(FORMAT_LABELS).map(([format, label]) => (
                            <div key={format} className="tw:flex tw:items-center tw:gap-2">
                                <Checkbox
                                    id={format}
                                    checked={selectedFormats[format]}
                                    onCheckedChange={() => handleFormatSelection(format)}
                                />
                                <Label htmlFor={format} className="tw:text-white tw:text-sm">{label}</Label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="tw:mt-6 tw:max-w-[500px]">
                    <span className="tw:text-sm tw:font-semibold">Prompt</span>
                    <p className="tw:text-sm tw:mb-1 tw:mt-2">
                        Describe the scene, movement, lighting, and style for best results.
                    </p>
                    <Textarea
                        placeholder="Describe the video, or add requirements such as a voice-over or music"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        className="tw:min-h-24 tw:mt-4 tw:bg-transparent tw:border-gray-600 tw:text-white tw:placeholder:text-gray-400 tw:focus:border-blue-400"
                    />
                    <PromptHelper
                        mediaType="VIDEO"
                        draft={prompt}
                        onSelect={setPrompt}
                        disabled={isGenerating}
                        leading={
                            isGenerating ? (
                                <Button variant="destructiveSoft" onClick={handleStopGenerate}>
                                    Stop Generation
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    onClick={handleGenerate}
                                    disabled={!prompt.trim() || loading || isGenerating}
                                >
                                    {isGenerating ? (
                                        <LoaderCircle size={16} className="tw:animate-spin" />
                                    ) : (
                                        <AIIcon size={24} />
                                    )}
                                    Generate Video
                                </Button>
                            )
                        }
                    />
                </div>

                {isGenerating && generationProgress.total > 0 && <GenerationProgress progress={generationProgress} />}

                {isMirroring && (
                    <Alert className="tw:text-left tw:mt-6">
                        <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
                        <AlertTitle className="tw:text-left">Please wait while we process your video.</AlertTitle>
                    </Alert>
                )}

                {generatedVideos.length > 0 && (
                    <div className="tw:mt-6">
                        <span className="tw:text-sm tw:font-semibold">Generated Videos</span>
                        <p className="tw:text-sm tw:text-gray-400 tw:mt-1">Click on a video to select it</p>
                        <div className="tw:flex tw:gap-4 tw:flex-wrap tw:mt-4">
                            {generatedVideos.map((video) => (
                                <VideoItem
                                    key={video.id}
                                    video={video}
                                    isSelected={selectedVideos.includes(video.id)}
                                    onSelect={handleSelect}
                                    onDelete={handleDelete}
                                    onRefine={setRefining}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {refining && (
                    <RefineMediaDialog
                        mediaType="VIDEO"
                        asset={refining}
                        campaignId={campaignId}
                        open
                        onClose={(pending) => {
                            setRefining(null)
                            if (pending) trackGenerating(pending, refining)
                        }}
                        onReplaced={(asset) => {
                            replaceAsset(refining, asset)
                            setRefining(null)
                        }}
                    />
                )}

                <ConfirmationModal
                    title="Delete"
                    variant="danger"
                    cancelText="Cancel"
                    confirmText="Delete"
                    onConfirm={handleConfirmDelete}
                    show={showDeleteConfirmationModal}
                    onCancel={() => setShowDeleteConfirmationModal(false)}
                    body="Are you sure you want to delete this video? This action cannot be undone."
                />
            </div>
        </div>
    )
}

export default AiVideoGeneration
